import { useTranslation } from "react-i18next";
import { CountUp } from "./CountUp";
import { useReveal } from "./useReveal";
import styles from "./StatsStrip.module.css";

const dotColours = ["#e60054", "#8353fd", "#66c9ff", "#ffc700", "#ff592c", "#870247"];

/** One dot a tool, scattered, drawing together round one centre. */
function ToolsVisual({ count }: { count: number }): React.JSX.Element {
  const dots = Array.from({ length: count }, (_, position) => {
    const angle = ((position / count) * 2 - 0.5) * Math.PI;
    return {
      colour: dotColours[position % dotColours.length],
      x: 40 + Math.cos(angle) * 18,
      y: 40 + Math.sin(angle) * 18,
      fromX: Math.cos(angle) * 38,
      fromY: Math.sin(angle) * 38,
    };
  });

  return (
    <svg viewBox="0 0 80 80" className={styles.visual} aria-hidden="true">
      <circle cx="40" cy="40" r="18" className={styles.orbit} />
      <circle cx="40" cy="40" r="5" className={styles.core} />
      {dots.map((dot, position) => (
        <circle
          key={position}
          cx={dot.x}
          cy={dot.y}
          r="6"
          fill={dot.colour}
          className={styles.toolDot}
          style={{ "--from-x": `${String(dot.fromX)}px`, "--from-y": `${String(dot.fromY)}px`, animationDelay: `${String(position * 0.12)}s` } as React.CSSProperties}
        />
      ))}
    </svg>
  );
}

/** Many sources, converging on one place. */
function PlacesVisual(): React.JSX.Element {
  const starts = [8, 22, 36, 50, 64, 78];

  return (
    <svg viewBox="0 0 80 86" className={styles.visual} aria-hidden="true">
      {starts.map((y, position) => (
        <path
          key={y}
          d={`M4 ${String(y)} C 36 ${String(y)}, 44 43, 70 43`}
          pathLength={1}
          className={styles.stream}
          style={{ animationDelay: `${String(position * 0.08)}s` }}
        />
      ))}
      <circle cx="70" cy="43" r="7" className={styles.target} />
    </svg>
  );
}

/** A spreadsheet, fading out, struck through. */
function SpreadsheetsVisual(): React.JSX.Element {
  const cells = Array.from({ length: 12 }, (_, index) => ({ x: 10 + (index % 3) * 21, y: 14 + Math.floor(index / 3) * 14 }));

  return (
    <svg viewBox="0 0 80 80" className={styles.visual} aria-hidden="true">
      {cells.map((cell, position) => (
        <rect key={position} x={cell.x} y={cell.y} width="18" height="11" rx="2" className={styles.cell} style={{ animationDelay: `${String(position * 0.05)}s` }} />
      ))}
      <path d="M6 70 L74 8" pathLength={1} className={styles.strike} />
    </svg>
  );
}

/**
 * The portal in numbers, each counting to its value (up or down) beside a small illustration as the strip scrolls into view. The number of
 * tools is the portal's own count, and is left out until it is known.
 *
 * @param props.animate - Whether the page's animation is on; the strip is shown still, in its final state, when it is not.
 * @param props.toolCount - How many AI tools the portal covers, or null when not known.
 */
export function StatsStrip({ animate, toolCount }: { animate: boolean; toolCount: number | null }): React.JSX.Element {
  const { t } = useTranslation();
  const [ref, state] = useReveal<HTMLElement>(animate);
  const stats = [
    ...(toolCount === null ? [] : [{ key: "tools", from: 0, to: toolCount, visual: <ToolsVisual count={toolCount} /> }]),
    { key: "places", from: 9, to: 1, visual: <PlacesVisual /> },
    { key: "spreadsheets", from: 37, to: 0, visual: <SpreadsheetsVisual /> },
  ];

  return (
    <section ref={ref} className={styles.stats} aria-label={t("signedOut.statsLabel")} data-state={state}>
      {stats.map(({ key, from, to, visual }, position) => (
        <div key={key} className={styles.stat} style={{ animationDelay: `${String(position * 0.12)}s` }}>
          <div className={styles.text}>
            <span className={styles.value}>
              <CountUp from={from} to={to} state={state} delayMs={position * 120 + 200} />
            </span>
            <span className={styles.label}>{t(`signedOut.stats.${key}`, { count: to })}</span>
          </div>
          {visual}
        </div>
      ))}
    </section>
  );
}
